// @flow
import * as React from 'react';
import { Base, Item, Label, List, TabContainer, TabIcon } from './styles';
import type { AnyIcon } from '~/core/constants/icons';
import keys from '~/core/constants/key-codes';
import { noop } from 'lodash/fp';

type TabRendererProps = {
    maybeIcon: ?React.Element<typeof TabIcon>,
    label: string,
    onClick: () => void
} & *;

export type TabRenderer = TabRendererProps => React.Node;

const IdentityTabRenderer: TabRenderer = ({ maybeIcon, label, onClick }) => {
    return (
        <div onClick={onClick}>
            {maybeIcon}
            {label}
        </div>
    );
};

const getLength = children => (Array.isArray(children) ? children.length : children ? 1 : 0);

type TabProps = {
    children: React.Node,
    icon?: AnyIcon,
    onClick?: () => void,
    selected?: boolean,
    name?: string,
    RenderTab?: TabRenderer
};

const Tab = ({ children, icon, onClick, selected, RenderTab = IdentityTabRenderer, ...rest }: TabProps) => {
    return (
        <Item selected={selected}>
            <RenderTab
                label={<Label>{children}</Label>}
                maybeIcon={icon && <TabIcon selected={selected} type={icon} />}
                onClick={onClick}
                {...rest}
            />
        </Item>
    );
};

type Props = {
    children?: React.ChildrenArray<React.Element<typeof Tab>>,
    maxWidth?: boolean,
    onTabChange?: number => void,
    selectedTabIndex: number,
    renderTab?: TabRenderer
};

export default class Tabs extends React.Component<Props, void> {
    static Item = Tab;
    static defaultProps = {
        selectedTabIndex: 0
    };

    keyDown = (e: SyntheticKeyboardEvent<>) => {
        const switchCase: { [number]: () => void } = {
            [keys.ARROW_LEFT]: () => {
                e.preventDefault();
                const { onTabChange = noop, selectedTabIndex } = this.props;
                if (selectedTabIndex > 0) {
                    onTabChange(selectedTabIndex - 1);
                }
            },
            [keys.ARROW_RIGHT]: () => {
                e.preventDefault();
                const { children, onTabChange = noop, selectedTabIndex } = this.props;
                if (selectedTabIndex < getLength(children) - 1) {
                    onTabChange(selectedTabIndex + 1);
                }
            }
        };

        if (typeof switchCase[e.keyCode] === 'function') {
            switchCase[e.keyCode]();
        } else if (e.keyCode >= keys.KEY_1 && e.keyCode <= keys.KEY_9) {
            const { children, onTabChange = noop, selectedTabIndex } = this.props;
            const digit = e.keyCode - 48; // cast the key number into the range [1-9]
            if (digit !== selectedTabIndex + 1 && digit <= getLength(children)) {
                onTabChange(digit - 1);
            }
        }
    };

    render() {
        const { children, maxWidth, onTabChange = noop, selectedTabIndex, renderTab } = this.props;
        const childrenWithProps = React.Children.map(children, (child, index) =>
            // $FlowFixMe
            React.cloneElement(child, {
                onClick: selectedTabIndex === index ? null : () => onTabChange(index),
                selected: selectedTabIndex === index,
                RenderTab: renderTab
            })
        );
        return (
            <Base onKeyDown={this.keyDown} tabIndex="0">
                <TabContainer>
                    <List maxWidth={maxWidth}>{childrenWithProps}</List>
                </TabContainer>
            </Base>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/tabs/index.js